export interface ZoneInfo {
  serviceId: string;
  serviceName: string;
  resourceId: string;
  zoneLabel: string;
  scenarioType: string;
  totalUnits: number;
  minPartySize: number;
  maxPartySize: number;
  unitCapacityMin: number;
  unitCapacityMax: number;
  pricing: { amount: number; unit: string; currency: string } | null;
}

export interface AvailabilityResult {
  hasAvailability: boolean;
  busyLevel: 'low' | 'medium' | 'high' | 'full';
  wordingKey: string;
}

/**
 * API base URL. Empty for dev (Vite proxy on same origin); set to the worker
 * URL in prod e.g. "https://api.example.com" via VITE_API_BASE_URL in .env.
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const BASE = `${API_BASE}/api`;
const CREDENTIALS: RequestCredentials = 'include';

export interface PublicVenueProfile {
  slug: string;
  venueId: string;
  name: string;
  timezone: string;
  branding: {
    appTitle: string;
    tagline: string;
    tileUnitLabel: string;
  };
  capabilities: {
    takeout: { enabled: boolean };
    publicPortal: {
      enabled: boolean;
      showTakeoutForm: boolean;
      lineId?: string;
    };
    allocation: { scenarios: readonly string[] };
  };
  zones: Array<{
    serviceId: string;
    resourceId: string;
    scenarioType: string;
    name: string;
    zoneLabel: string;
    capacity: number;
    minPartySize: number;
    maxPartySize: number;
    pricing: { amount: number; unit: string; currency: string } | null;
  }>;
}

export async function fetchPublicVenueProfile(): Promise<PublicVenueProfile> {
  const res = await fetch(`${BASE}/public/venue-profile`, {
    credentials: CREDENTIALS,
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new Error(err.message ?? `venue-profile: HTTP ${res.status}`);
  }
  return (await res.json()) as PublicVenueProfile;
}

export async function fetchVenueZones(venueId: string): Promise<ZoneInfo[]> {
  const res = await fetch(`${BASE}/staff/venue-zones?venueId=${venueId}`, {
    credentials: CREDENTIALS,
  });
  if (!res.ok) throw new Error(`venue-zones: ${res.status}`);
  const data = (await res.json()) as { zones: ZoneInfo[] };
  return data.zones;
}

export async function fetchAvailability(params: {
  venueId: string;
  serviceId: string;
  scenarioType: string;
  resourceId: string;
  partySize: number;
  startTime: string;
  endTime: string;
}): Promise<AvailabilityResult> {
  const qs = new URLSearchParams({
    venueId: params.venueId,
    serviceId: params.serviceId,
    scenarioType: params.scenarioType,
    resourceId: params.resourceId,
    partySize: String(params.partySize),
    startTime: params.startTime,
    endTime: params.endTime,
  });
  const res = await fetch(`${BASE}/public/availability?${qs}`, {
    credentials: CREDENTIALS,
  });
  if (!res.ok) throw new Error(`availability: ${res.status}`);
  return (await res.json()) as AvailabilityResult;
}

export interface TakeoutRequestInput {
  venueId: string;
  customerRef: string;
  pickupTime: string;
  itemNote: string;
}

export interface TakeoutRequestCreated {
  requestId: string;
  status: 'submitted';
}

export async function submitTakeoutRequest(
  input: TakeoutRequestInput,
): Promise<TakeoutRequestCreated> {
  const res = await fetch(`${BASE}/public/takeout-requests`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: CREDENTIALS,
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as TakeoutRequestCreated;
}
